// src/pages/msp-inventory/pages/reports/SfNotZeroedPage.jsx
import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle } from "lucide-react";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { exportSkuReportCsv } from "../../utils/exportCsv";
import ClickableSkuCell from "../../components/reports/ClickableSkuCell";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getSfNotZeroed } from "../../services/catalogService";
import { useImageBackfill } from "../../hooks/useImageBackfill";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import LoadingGame from "../../components/reports/LoadingGame";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);

const SfNotZeroedPage = ({ onOpenSku }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [zeroingId, setZeroingId] = useState(null);
    const [savedAt, setSavedAt] = useState(null);

    const cacheKey = buildCacheKey("sf-not-zeroed", {});

    const load = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getSfNotZeroed();
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await load(true);
    };

    const patchSfItem = (id, patch) => {
        setData((prev) => prev ? {
            ...prev,
            items: prev.items.map((i) => i.id === id ? { ...i, ...patch } : i),
        } : prev);
    };

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(data?.items || [], { key: "sf_stock", dir: "desc" }, "reports:sf-not-zeroed:table");

    useImageBackfill(sorted, patchSfItem, { idKey: "id" });

    const handleZero = async (item) => {
        setZeroingId(item.id);
        try {
            const resp = await api("/api/walmart/api/inventory/sf", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ catalog_item_id: item.id, sku: item.sku, quantity: 0 }),
            });
            if (!resp.ok) throw new Error("Failed to zero SF inventory");
            setData((d) => {
                const next = { ...d, items: d.items.filter((i) => i.id !== item.id), count: d.count - 1 };
                setCachedReport(cacheKey, next); // keep the cache in sync with this local edit
                return next;
            });
        } catch (e) {
            alert(e.message || "Failed to zero SF inventory");
        } finally {
            setZeroingId(null);
        }
    };

    return (
        <div className="max-w-4xl mx-auto py-8 px-6">
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <AlertCircle size={22} className="text-red-500" /> SF Not Zeroed
                    </h1>
                    <p className="text-gray-500 text-sm">Items with both WFS and Seller-Fulfilled inventory — SF should usually be zeroed to avoid overselling / duplicate stock</p>
                </div>
                <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
            </div>

            {data?.items?.length > 0 && (
                <div className="flex justify-end mb-4">
                    <ExportCsvButton onExport={() => exportSkuReportCsv({
                        filename: `sf_not_zeroed_${new Date().toISOString().slice(0, 10)}.csv`,
                        rows: sorted,
                        idKey: "id",
                        headers: ["Item", "SKU", "SF Stock", "WFS Stock"],
                        getRowValues: (r) => [r.title, r.sku, r.sf_stock, r.wfs_stock],
                    })} />
                </div>
            )}

            {loading ? (
                <LoadingGame label="Checking SF inventory..." />
            ) : (data?.items || []).length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
                    <CheckCircle size={32} className="text-green-500 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nothing to fix — no items have leftover SF stock alongside WFS stock.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                <SortableTH label="Item" sortKey="title" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="SF Stock" sortKey="sf_stock" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="WFS Stock" sortKey="wfs_stock" sortConfig={sortConfig} onSort={requestSort} />
                                <th className="pb-2 font-semibold"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((item) => (
                                <tr key={item.id} className="border-b border-gray-50">
                                    <td className="py-2"><ClickableSkuCell item={{ ...item, catalog_item_id: item.id }} onOpen={onOpenSku} /></td>
                                    <td className="py-2 font-bold text-red-600">{item.sf_stock}</td>
                                    <td className="py-2">{item.wfs_stock}</td>
                                    <td className="py-2 text-right">
                                        <button onClick={() => handleZero(item)} disabled={zeroingId === item.id}
                                            className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg font-bold hover:bg-red-100 transition disabled:opacity-50">
                                            {zeroingId === item.id ? "Zeroing..." : "Zero SF Now"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                </div>
            )}
        </div>
    );
};

export default SfNotZeroedPage;